// ============================================================================
// Feuille du noyau : CALCUL MATRICIEL (refonte, lot 5.8, moteur v30).
// ============================================================================
// Le modèle donne les matrices de l'énoncé (`kernel.matrices`), ce qu'il faut calculer
// (`kernel.start` : « AB », « π₀P² », « [1, 1 ; 0, 1]² », ou l'équation « AX = B ») et la
// réponse (« AB = [8 ; 9] »). L'élève écrit des chaînes d'égalités « AB = … = … » ; chaque
// membre est évalué EXACTEMENT (rationnels), et chaque maillon doit être un pas reconnu :
//
//   Remplacer une matrice par sa valeur (ou l'inverse)       1   matrix.substitute@1
//   Écrire une puissance (A² = A × A, A⁴ = A² × A²)          1   matrix.power@1
//   Ligne par colonne (le produit, coefficient par coefficient
//     écrit comme une somme de produits)                     2   matrix.product@1
//   Coefficient par coefficient (somme, nombre × matrice)    1   matrix.entrywise@1
//   Calculer (les coefficients, un nombre)                   1   matrix.compute@1
//   Inverse d'une 2 × 2 : (1 ÷ det A) × [d, −b ; −c, a]       3   matrix.inverse@1
//   Vérifier un inverse (AM = I écrit avant)                 2   matrix.inverse_check@1
//   Déterminant 2 × 2 : a × d − b × c                         2   matrix.det@1
//   Transposer, multiplier par I, factoriser par un nombre   1
//   Résoudre AX = B : X = A⁻¹B                                2   matrix.solve@1
//   Nommer un calcul intermédiaire (π₁ = π₀P)                1   matrix.define@1
//   moteur v52 — un coefficient « (M²)₁₃ » :
//   Ligne i par colonne j (le coefficient seul, terme à terme) 2   matrix.entry_product@1
//   Lire le coefficient (la matrice est écrite)              1   matrix.entry@1
//
// Un saut (« AB = [8 ; 9] » sans le calcul) : « Montre le calcul ». Une ligne fausse : la
// taille, l'ordre du produit, le produit coefficient par coefficient, ou le coefficient qui
// ne va pas — jamais la bonne valeur.
// ============================================================================

import type { Chip } from './lexer';
import type { ScratchModel } from './model';
import type { Line } from './tree';
import * as Q from './kernel/exact';
import {
  canon,
  evalM,
  factors,
  identity,
  isIdentityExpr,
  isMatName,
  isNumericLit,
  isSupAtom,
  MAT_NUM_RE,
  MatError,
  MatIncomplete,
  MatSyntaxError,
  isEntryAtom,
  matLineAtoms,
  matText,
  mulVals,
  namesIn,
  numOf,
  opCount,
  parseMatLine,
  powerFactors,
  sameMultiset,
  termValues,
  valEq,
  detOf,
  inverseOf,
  type MExpr,
  type MVal,
  type MatEnv,
} from './kernel/matrix';

export const MATRIX_STEP_IDS = {
  substitute: 'matrix.substitute@1',
  power: 'matrix.power@1',
  identity: 'matrix.identity@1',
  product: 'matrix.product@1',
  entrywise: 'matrix.entrywise@1',
  transpose: 'matrix.transpose@1',
  inverse: 'matrix.inverse@1',
  inverseCheck: 'matrix.inverse_check@1',
  det: 'matrix.det@1',
  compute: 'matrix.compute@1',
  factor: 'matrix.factor@1',
  solve: 'matrix.solve@1',
  define: 'matrix.define@1',
  expand: 'matrix.expand@1',
  /** moteur v52 */
  entryProduct: 'matrix.entry_product@1',
  /** moteur v52 */
  entry: 'matrix.entry@1',
} as const;
type StepKind = keyof typeof MATRIX_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  substitute: {
    label: 'Remplacer',
    hora: 1,
    why: 'Une matrice remplacée par sa valeur (ou une valeur reconnue).',
  },
  power: {
    label: 'Écrire la puissance',
    hora: 1,
    why: 'Une puissance est un produit de facteurs égaux : A² = A × A, A⁴ = A² × A².',
  },
  identity: {
    label: 'Multiplier par I',
    hora: 1,
    why: 'I est neutre : I × M = M × I = M, et Iᵏ = I.',
  },
  product: {
    label: 'Ligne par colonne',
    hora: 2,
    why: 'Le coefficient (i, j) d’un produit, c’est la ligne i de la première matrice par la colonne j de la seconde : on multiplie terme à terme et on ajoute.',
  },
  entrywise: {
    label: 'Coefficient par coefficient',
    hora: 1,
    why: 'Une somme de matrices, ou un nombre fois une matrice, se calcule coefficient par coefficient.',
  },
  transpose: {
    label: 'Transposer',
    hora: 1,
    why: 'Les lignes de la matrice deviennent ses colonnes.',
  },
  inverse: {
    label: 'Inverse d’une matrice 2 × 2',
    hora: 3,
    why: 'Si det A ≠ 0 : A⁻¹ = (1 ÷ det A) × [d, −b ; −c, a] — on échange a et d, on change le signe de b et c.',
  },
  inverseCheck: { label: 'Vérifier l’inverse', hora: 2, why: 'AM = I : M est l’inverse de A.' },
  det: { label: 'Déterminant', hora: 2, why: 'det [a, b ; c, d] = a × d − b × c.' },
  compute: { label: 'Calculer', hora: 1, why: 'Les calculs faits.' },
  factor: {
    label: 'Factoriser par un nombre',
    hora: 1,
    why: 'Tous les coefficients sont des multiples du même nombre.',
  },
  solve: {
    label: 'Multiplier par l’inverse',
    hora: 2,
    why: 'AX = B avec A inversible : X = A⁻¹B — on multiplie à GAUCHE par A⁻¹, l’ordre compte.',
  },
  define: { label: 'Nommer un calcul', hora: 1, why: 'Un calcul intermédiaire reçoit un nom.' },
  expand: {
    label: 'Développer',
    hora: 1,
    why: 'Le produit se distribue sur la somme en gardant l’ordre des facteurs : A(A − 2I) = A² − 2A, et AI = A.',
  },
  entryProduct: {
    label: 'Ligne par colonne',
    hora: 2,
    why: 'Le coefficient (i, j) d’un produit, c’est la ligne i de la première matrice par la colonne j de la seconde : on multiplie terme à terme et on ajoute.',
  },
  entry: {
    label: 'Lire le coefficient',
    hora: 1,
    why: 'Le coefficient (i, j) se lit à la ligne i, colonne j de la matrice écrite.',
  },
};

const CARD = 'Calcul matriciel';

export interface KMatVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  mmove?: MMove;
}

interface MMove {
  /** clé de l'objet calculé (« AB », « π₁ », « det(A) ») */
  key: string;
  members: MExpr[];
  /** une matrice nommée par la ligne (π₁ = π₀P) */
  define?: { name: string; value: MVal };
  final?: boolean;
}

interface MatObject {
  key: string;
  start: MExpr;
  value: MVal;
  /** dernière forme écrite (null : la forme de départ) */
  form: MExpr | null;
  /** toutes les formes écrites ou données (signatures `sig`) */
  written: Set<string>;
  target: boolean;
  relevant: boolean;
}

export interface KMatState {
  model: ScratchModel;
  env: MatEnv;
  objects: Map<string, MatObject>;
  /** l'objet cherché */
  target: MatObject;
  /** équation de départ (AX = B), X inconnue */
  equation: { coef: MExpr; right: MExpr; side: 'left' | 'right'; unknown: string } | null;
  answerLast: MExpr;
  /** clés des objets que le corrigé calcule */
  relevantKeys: Set<string>;
  traps: {
    key: string;
    text: string;
    value: MVal | null;
    line: Line;
    title: string;
    why: string;
  }[];
  /** matrices de l'énoncé données par une matrice écrite */
  givenLit: Set<string>;
  /** lignes de l'énoncé (« A = [1, 2 ; 3, 4] », le départ) : les recopier n'avance pas */
  givenTexts: Set<string>;
  /** les lignes validées (membres) */
  lines: { key: string; members: MExpr[] }[];
  known: string[][];
  concluded: boolean;
  path: string[];
}

/* ------------------------------ lecture ------------------------------ */

export function kmatLineAtoms(text: string): string[] {
  return matLineAtoms(text);
}
/** Ligne de feuille (texte) pour l'app : l'écriture de l'élève, mise en forme. */
export function kmatLine(text: string): Line {
  return lineOf(kmatLineAtoms(text));
}
const lineOf = (atoms: string[]): Line => ({ dis: [{ sides: [{ k: 's', n: matText(atoms) }] }] });

function parseText(text: string): MExpr[] {
  return parseMatLine(kmatLineAtoms(text));
}

/* ------------------------------ état ------------------------------ */

const strip = (e: MExpr): MExpr => (e.k === 'paren' ? strip(e.e) : e);

/**
 * Signature d'une écriture : sa forme canonique, les puissances regroupées (A × A et A²
 * ont la même : une égalité déjà écrite avec l'une vaut pour l'autre).
 */
export function sig(e: MExpr): string {
  const pf = powerFactors(e);
  return pf ? pf.map((f) => (f.n === 1 ? f.base : `${f.base}^${f.n}`)).join('·') : canon(e);
}
const prodOf = (fs: MExpr[]): MExpr =>
  fs.slice(1).reduce<MExpr>((l, r) => ({ k: 'mul', l, r }), fs[0]!);

export function createKMatState(model: ScratchModel): KMatState {
  const spec = model.kernel!;
  const env: MatEnv = new Map();
  const objects = new Map<string, MatObject>();
  const mk = (key: MExpr, start: MExpr, value: MVal, target: boolean): MatObject => ({
    key: canon(key),
    start,
    value,
    form: null,
    written: new Set([sig(key), sig(start)]),
    target,
    relevant: true,
  });
  const givenLit = new Set<string>();
  const givenTexts = new Set<string>();
  for (const [name, text] of Object.entries(spec.matrices ?? {})) {
    const e = parseText(text);
    if (e.length !== 1) throw new Error(`Matrice ${name} : une seule expression attendue.`);
    const v = evalM(e[0]!, env);
    givenTexts.add(matText(kmatLineAtoms(`${name} = ${text}`)));
    const key = parseText(name);
    const k0 = strip(key[0]!);
    if (key.length !== 1) throw new Error(`Nom de matrice illisible : ${name}`);
    if (k0.k !== 'name') {
      // une puissance donnée par l'énoncé (A⁻¹ = …, A² = …) : une égalité établie
      const base = strip(k0.k === 'pow' || k0.k === 'tr' ? k0.e : k0);
      if (base.k !== 'name') throw new Error(`Nom de matrice illisible : ${name}`);
      if (!env.has(base.n)) {
        if (k0.k !== 'pow' || k0.n !== -1 || v.t !== 'm')
          throw new Error(`${name} : donne d’abord la matrice ${base.n}.`);
        const inv = inverseOf(v.a);
        if (!inv) throw new Error(`${name} n’est pas inversible.`);
        env.set(base.n, { t: 'm', r: v.r, c: v.c, a: inv });
      }
      if (!valEq(evalM(k0, env), v)) throw new Error(`${name} ne correspond pas à ${base.n}.`);
      objects.set(canon(k0), mk(k0, e[0]!, v, false));
      continue;
    }
    env.set(name, v);
    // une matrice définie par un calcul (B = A − 2I) : un objet que l'élève peut calculer
    if (strip(e[0]!).k === 'lit') givenLit.add(name);
    else objects.set(name, mk(k0, e[0]!, v, false));
  }
  givenTexts.add(matText(kmatLineAtoms(spec.start)));
  const startMembers = parseText(spec.start);
  let equation: KMatState['equation'] = null;
  let target: MatObject;
  const tgtExpr = parseText(spec.goal.target);
  if (tgtExpr.length !== 1) throw new Error('La cible est une matrice ou un calcul.');
  if (startMembers.length === 2) {
    // AX = B : X inconnue, à gauche ou à droite du produit
    const unknown = canon(tgtExpr[0]!);
    const left = strip(startMembers[0]!);
    if (left.k !== 'mul') throw new Error('Équation attendue : AX = B ou XA = B.');
    const side = canon(left.r) === unknown ? 'left' : canon(left.l) === unknown ? 'right' : null;
    if (!side) throw new Error('L’inconnue doit être un facteur du produit : AX = B.');
    const coef = side === 'left' ? left.l : left.r;
    const c = evalM(coef, env);
    const b = evalM(startMembers[1]!, env);
    const inv: MVal = evalM({ k: 'pow', e: coef, n: -1, text: '⁻¹' }, env);
    void c;
    const x = side === 'left' ? mulVals(inv, b) : mulVals(b, inv);
    env.set(unknown, x);
    equation = { coef, right: startMembers[1]!, side, unknown };
    target = mk(tgtExpr[0]!, tgtExpr[0]!, x, true);
    objects.set(unknown, target);
  } else {
    const start = startMembers[0]!;
    const value = evalM(start, env);
    target = mk(tgtExpr[0]!, start, value, true);
    objects.set(target.key, target);
    if (canon(start) !== target.key) {
      objects.set(canon(start), target);
      // l'app affiche la définition de la cible (« π₂ = π₀P² ») parmi les lignes de l'énoncé
      givenTexts.add(matText(kmatLineAtoms(`${spec.goal.target} = ${spec.start}`)));
    }
    // la cible est un nom nouveau (π₂, X₁, C) : il vaut le calcul
    const t = strip(tgtExpr[0]!);
    if (t.k === 'name' && !env.has(t.n)) env.set(t.n, value);
  }
  const answer = parseText(spec.answer);
  const ks: KMatState = {
    model,
    env,
    objects,
    target,
    equation,
    answerLast: answer[answer.length - 1]!,
    relevantKeys: new Set([...objects.keys()]),
    traps: [],
    givenLit,
    givenTexts,
    lines: [],
    known: [],
    concluded: false,
    path: spec.path ?? [],
  };
  for (const t of ks.path) {
    try {
      const ms = parseText(t);
      ks.relevantKeys.add(canon(ms[0]!));
    } catch {
      /* lu par la porte */
    }
  }
  ks.traps = (model.traps ?? []).flatMap((t) => {
    try {
      const ms = parseText(t.line);
      let value: MVal | null = null;
      try {
        value = evalM(ms[ms.length - 1]!, env);
      } catch {
        value = null;
      }
      return [
        {
          key: canon(ms[0]!),
          text: matText(kmatLineAtoms(t.line)),
          value,
          line: kmatLine(t.line),
          title: t.title,
          why: t.why,
        },
      ];
    } catch {
      return [];
    }
  });
  ks.known = [spec.answer, ...ks.path, ...(model.traps ?? []).map((t) => t.line)].flatMap((t) => {
    try {
      return [kmatLineAtoms(t)];
    } catch {
      return [];
    }
  });
  return ks;
}

/* ------------------------------ valeurs ------------------------------ */

function valOf(ks: KMatState, e: MExpr): MVal | null {
  try {
    return evalM(e, ks.env);
  } catch {
    return null;
  }
}

function prodVal(ks: KMatState, fs: MExpr[]): MVal | null {
  let v: MVal = { t: 's', q: Q.ONE };
  for (const f of fs) {
    const x = valOf(ks, f);
    if (!x) return null;
    try {
      v = mulVals(v, x);
    } catch {
      return null;
    }
  }
  return v;
}

/** facteurs d'un produit, puissances entières (≤ 12) dépliées : π₀P² → π₀, P, P */
function efactors(e: MExpr): MExpr[] {
  return factors(e).flatMap((f) => {
    const x = strip(f);
    if (x.k === 'pow' && x.n >= 2 && x.n <= 12)
      return Array.from({ length: x.n }, () => strip(x.e));
    return [x];
  });
}

/* ------------------------------ les pas ------------------------------ */

type Cls = { ok: true; kinds: StepKind[] } | { ok: false; title: string; message: string };
const pass = (...kinds: StepKind[]): Cls => ({ ok: true, kinds });

/**
 * X et V désignent-ils la même matrice, d'après l'énoncé ou une ligne déjà écrite ?
 * Une matrice de l'énoncé et sa matrice écrite, I et la matrice identité, ou deux
 * écritures reliées par une ligne validée (« A² = [1, 2 ; 0, 1] »). Jamais un calcul
 * fait de tête.
 */
function knownEqual(ks: KMatState, X0: MExpr, V0: MExpr): boolean {
  const X = strip(X0);
  const V = strip(V0);
  const vx = valOf(ks, X);
  const vv = valOf(ks, V);
  if (!vx || !vv || !valEq(vx, vv)) return false;
  const sx = sig(X);
  const sv = sig(V);
  if (sx === sv) return true;
  const named = (N: MExpr, L: MExpr): boolean =>
    isNumericLit(L) && ((N.k === 'name' && ks.givenLit.has(N.n)) || isIdentityExpr(N));
  if (named(X, V) || named(V, X)) return true;
  for (const o of new Set(ks.objects.values()))
    if (o.written.has(sx) && o.written.has(sv)) return true;
  return false;
}

/** le déterminant de M a-t-il été calculé (une ligne « det(A) = 1 ») ? */
function detKnown(ks: KMatState, M: MExpr): boolean {
  const o = ks.objects.get(canon({ k: 'det', e: strip(M) }));
  return !!o && !!o.form && numOf(o.form) !== null;
}

/**
 * P → N est-il l'inverse d'une 2 × 2 écrit avec la formule ? Il faut le déterminant
 * calculé avant : il dit si la matrice est inversible.
 */
function inverseFormula(ks: KMatState, P0: MExpr, N0: MExpr): 'ok' | 'needDet' | 'no' {
  const P = strip(P0);
  const N = strip(N0);
  if (P.k !== 'pow' || P.n !== -1) return 'no';
  const base = valOf(ks, P.e);
  if (!base || base.t !== 'm' || base.r !== 2 || base.c !== 2) return 'no';
  const [[a, b], [c, d]] = base.a as [[Q.Rat, Q.Rat], [Q.Rat, Q.Rat]];
  const adj: MVal = {
    t: 'm',
    r: 2,
    c: 2,
    a: [
      [d, Q.neg(b)],
      [Q.neg(c), a],
    ],
  };
  const det = detOf(base.a);
  const adjOk = (L: MExpr): boolean => {
    const v = valOf(ks, L);
    return !!v && v.t === 'm' && valEq(v, adj);
  };
  let formula = false;
  const fs = factors(N);
  const lits = fs.filter((f) => strip(f).k === 'lit');
  if (lits.length === 1 && fs.length > 1 && adjOk(lits[0]!)) {
    const k = prodVal(
      ks,
      fs.filter((f) => strip(f).k !== 'lit'),
    );
    if (k && k.t === 's' && Q.eq(Q.mul(k.q, det), Q.ONE)) formula = true;
  }
  if (N.k === 'div' && adjOk(N.l)) {
    const k = valOf(ks, N.r);
    if (k && k.t === 's' && Q.eq(k.q, det)) formula = true;
  }
  // det = 1 : l'inverse est la matrice [d, −b ; −c, a] elle-même
  if (N.k === 'lit' && Q.eq(det, Q.ONE) && adjOk(N)) formula = true;
  if (!formula) return 'no';
  return detKnown(ks, P.e) ? 'ok' : 'needDet';
}

type SubstResult = 'same' | 'ok' | 'inverse' | 'needDet' | 'no';
const RANK: Record<SubstResult, number> = { same: 0, ok: 1, inverse: 2, needDet: 3, no: 4 };
const worst = (a: SubstResult, b: SubstResult): SubstResult => (RANK[a] >= RANK[b] ? a : b);

/**
 * P → N en REMPLAÇANT des matrices : un nom par sa valeur (ou l'inverse), un calcul déjà
 * écrit par son résultat, un groupe de facteurs par le nom qui le désigne (π₀P → π₁), un
 * A⁻¹ par la formule de l'inverse. Les facteurs se correspondent dans l'ordre (le produit
 * ne commute pas).
 */
function subst(ks: KMatState, P0: MExpr, N0: MExpr): SubstResult {
  const P = strip(P0);
  const N = strip(N0);
  if (sig(P) === sig(N)) return 'same';
  if (P.k === 'mul' || N.k === 'mul') {
    const fp = efactors(P);
    const fn = efactors(N);
    if (fp.length > 10 || fn.length > 10) return 'no';
    // découpage des deux suites de facteurs en groupes qui se correspondent
    const memo = new Map<string, SubstResult>();
    const go = (i: number, j: number): SubstResult => {
      if (i === fp.length && j === fn.length) return 'same';
      if (i === fp.length || j === fn.length) return 'no';
      const key = `${i},${j}`;
      const m = memo.get(key);
      if (m) return m;
      let best: SubstResult = 'no';
      for (let a = 1; i + a <= fp.length; a++)
        for (let b = 1; j + b <= fn.length; b++) {
          if (a > 1 && b > 1) continue;
          const rp = fp.slice(i, i + a);
          const rn = fn.slice(j, j + b);
          let pair: SubstResult;
          if (a === 1 && b === 1) pair = subst(ks, rp[0]!, rn[0]!);
          else if (knownEqual(ks, prodOf(rp), prodOf(rn))) pair = 'ok';
          else if (a === 1) {
            const inv = inverseFormula(ks, rp[0]!, prodOf(rn));
            pair = inv === 'ok' ? 'inverse' : inv;
          } else pair = 'no';
          if (pair === 'no') continue;
          const rest = go(i + a, j + b);
          if (rest === 'no') continue;
          const r = worst(pair, rest);
          if (RANK[r] < RANK[best]) best = r;
        }
      memo.set(key, best);
      return best;
    };
    return go(0, 0);
  }
  if (P.k === N.k && (P.k === 'add' || P.k === 'sub' || P.k === 'div')) {
    const n = N as Extract<MExpr, { k: 'add' | 'sub' | 'div' }>;
    return worst(subst(ks, P.l, n.l), subst(ks, P.r, n.r));
  }
  if (P.k === N.k && (P.k === 'neg' || P.k === 'tr' || P.k === 'det'))
    return subst(ks, P.e, (N as Extract<MExpr, { k: 'neg' | 'tr' | 'det' }>).e);
  if (P.k === 'pow' && N.k === 'pow' && P.n === N.n) {
    const r = subst(ks, P.e, N.e);
    if (r !== 'no') return r;
  }
  if (P.k === 'lit' && N.k === 'lit') {
    if (P.rows.length !== N.rows.length || P.rows[0]!.length !== N.rows[0]!.length) return 'no';
    let out: SubstResult = 'same';
    for (let i = 0; i < P.rows.length; i++)
      for (let j = 0; j < P.rows[0]!.length; j++)
        out = worst(out, subst(ks, P.rows[i]![j]!, N.rows[i]![j]!));
    return out;
  }
  if (knownEqual(ks, P, N)) return 'ok';
  const inv = inverseFormula(ks, P, N);
  return inv === 'ok' ? 'inverse' : inv;
}

/** le produit de deux matrices écrit coefficient par coefficient ? */
function productExpansion(
  ks: KMatState,
  P: MExpr,
  N: MExpr,
): 'ok' | { i: number; j: number } | null {
  const fs = efactors(P);
  if (fs.length !== 2) return null;
  const L = valOf(ks, fs[0]!);
  const R = valOf(ks, fs[1]!);
  if (!L || !R || L.t !== 'm' || R.t !== 'm') return null;
  const n = strip(N);
  if (n.k !== 'lit' || n.rows.length !== L.r || n.rows[0]!.length !== R.c) return null;
  for (let i = 0; i < L.r; i++)
    for (let j = 0; j < R.c; j++) {
      const ex: Q.Rat[] = [];
      for (let k = 0; k < L.c; k++) ex.push(Q.mul(L.a[i]![k]!, R.a[k]![j]!));
      if (!termsMatch(n.rows[i]![j]!, ex)) return { i, j };
    }
  return 'ok';
}

/**
 * Un coefficient écrit comme la somme des termes attendus (les termes nuls peuvent
 * manquer) ; un nombre seul seulement quand il n'y avait qu'un terme non nul.
 */
function termsMatch(e: MExpr, ex: Q.Rat[]): boolean {
  const terms = termValues(e);
  if (!terms) return false;
  const nz = (xs: Q.Rat[]) => xs.filter((x) => !Q.isZero(x));
  return sameMultiset(nz(terms), nz(ex)) && (terms.length > 1 || nz(ex).length <= 1);
}

/** une combinaison de matrices : Σ coef × matrice (sommes, différences, nombre × matrice) */
function linearTerms(ks: KMatState, e: MExpr): { k: Q.Rat; v: MVal }[] | null {
  const x = strip(e);
  const scale = (ts: { k: Q.Rat; v: MVal }[] | null, q: Q.Rat) =>
    ts ? ts.map((t) => ({ k: Q.mul(t.k, q), v: t.v })) : null;
  switch (x.k) {
    case 'add':
    case 'sub': {
      const l = linearTerms(ks, x.l);
      const r = linearTerms(ks, x.r);
      if (!l || !r) return null;
      return [...l, ...(x.k === 'sub' ? scale(r, Q.neg(Q.ONE))! : r)];
    }
    case 'neg':
      return scale(linearTerms(ks, x.e), Q.neg(Q.ONE));
    case 'mul': {
      const l = valOf(ks, x.l);
      const r = valOf(ks, x.r);
      if (l?.t === 's' && r && r.t !== 's') return scale(linearTerms(ks, x.r), l.q);
      if (r?.t === 's' && l && l.t !== 's') return scale(linearTerms(ks, x.l), r.q);
      break;
    }
    case 'div': {
      const r = valOf(ks, x.r);
      if (r?.t === 's' && !Q.isZero(r.q)) return scale(linearTerms(ks, x.l), Q.div(Q.ONE, r.q)!);
      break;
    }
    default:
      break;
  }
  // une matrice nommée ou écrite, jamais un produit de matrices calculé de tête
  if (x.k !== 'name' && x.k !== 'lit') return null;
  const v = valOf(ks, x);
  return v && v.t !== 's' ? [{ k: Q.ONE, v }] : null;
}

const coefAt = (v: MVal, i: number, j: number): Q.Rat =>
  v.t === 'm' ? v.a[i]![j]! : v.t === 'id' ? (i === j ? v.s : Q.ZERO) : v.q;

/** une somme de matrices écrite coefficient par coefficient (« [1 + 0, 2 + 1 ; …] ») ? */
function entrywiseExpansion(ks: KMatState, P: MExpr, N: MExpr): boolean {
  const n = strip(N);
  if (n.k !== 'lit') return false;
  const ts = linearTerms(ks, P);
  if (!ts || ts.length < 2) return false;
  const r = n.rows.length;
  const c = n.rows[0]!.length;
  if (ts.some((t) => t.v.t === 'm' && (t.v.r !== r || t.v.c !== c))) return false;
  for (let i = 0; i < r; i++)
    for (let j = 0; j < c; j++)
      if (
        !termsMatch(
          n.rows[i]![j]!,
          ts.map((t) => Q.mul(t.k, coefAt(t.v, i, j))),
        )
      )
        return false;
  return true;
}

/**
 * Forme développée d'une expression de matrices : Σ coef × mot, un mot étant la suite
 * des matrices du produit (l'ordre compte, I disparaît). null si l'expression sort de
 * ce cadre.
 */
function nf(ks: KMatState, e: MExpr): Map<string, Q.Rat> | null {
  const x = strip(e);
  const one = (w: string, q: Q.Rat = Q.ONE) => new Map([[w, q]]);
  const add = (a: Map<string, Q.Rat>, b: Map<string, Q.Rat>, sign: 1 | -1) => {
    const out = new Map(a);
    b.forEach((q, w) => {
      const v = Q.add(out.get(w) ?? Q.ZERO, sign === 1 ? q : Q.neg(q));
      if (Q.isZero(v)) out.delete(w);
      else out.set(w, v);
    });
    return out;
  };
  switch (x.k) {
    case 'num':
      return one('', x.q);
    case 'name':
      return one(isIdentityExpr(x) ? '' : x.n);
    case 'lit':
      return one(canon(x));
    case 'neg': {
      const a = nf(ks, x.e);
      return a ? add(new Map(), a, -1) : null;
    }
    case 'add':
    case 'sub': {
      const a = nf(ks, x.l);
      const b = nf(ks, x.r);
      return a && b ? add(a, b, x.k === 'add' ? 1 : -1) : null;
    }
    case 'mul': {
      const a = nf(ks, x.l);
      const b = nf(ks, x.r);
      if (!a || !b || a.size * b.size > 64) return null;
      let out = new Map<string, Q.Rat>();
      a.forEach((qa, wa) =>
        b.forEach((qb, wb) => {
          out = add(out, one([wa, wb].filter(Boolean).join('·'), Q.mul(qa, qb)), 1);
        }),
      );
      return out;
    }
    case 'pow': {
      if (isIdentityExpr(x)) return one('');
      if (x.n < 2 || x.n > 6) return one(canon(x));
      let out: Map<string, Q.Rat> | null = one('');
      for (let i = 0; i < x.n && out; i++)
        out = nf(ks, { k: 'mul', l: { k: 'paren', e: buildNf(out) }, r: x.e });
      return out;
    }
    case 'div': {
      const r = valOf(ks, x.r);
      const a = nf(ks, x.l);
      if (!a || !r || r.t !== 's' || Q.isZero(r.q)) return null;
      const out = new Map<string, Q.Rat>();
      a.forEach((q, w) => out.set(w, Q.div(q, r.q)!));
      return out;
    }
    case 'det': {
      const v = valOf(ks, x);
      return v && v.t === 's' ? one('', v.q) : null;
    }
    default:
      return one(canon(x));
  }
}

/** une forme développée réécrite en expression (pour enchaîner les produits) */
function buildNf(m: Map<string, Q.Rat>): MExpr {
  const terms: MExpr[] = [...m.entries()].map(([w, q]) => {
    const coef: MExpr = { k: 'num', q, text: '' };
    if (!w) return coef;
    const ws = w.split('·').map((n): MExpr => ({ k: 'name', n }));
    return { k: 'mul', l: coef, r: prodOf(ws) };
  });
  if (!terms.length) return { k: 'num', q: Q.ZERO, text: '0' };
  return terms.slice(1).reduce<MExpr>((l, r) => ({ k: 'add', l, r }), terms[0]!);
}

const sameNf = (a: Map<string, Q.Rat> | null, b: Map<string, Q.Rat> | null): boolean =>
  !!a && !!b && a.size === b.size && [...a].every(([w, q]) => !!b.get(w) && Q.eq(b.get(w)!, q));

/** sommes, différences, nombre × matrice : pas de produit de deux matrices */
function isLinear(ks: KMatState, e: MExpr): boolean {
  return linearTerms(ks, e) !== null;
}

/** un produit de deux matrices (pas un nombre × matrice), à calculer ligne par colonne */
function hasMatrixProduct(ks: KMatState, e: MExpr): boolean {
  const x = strip(e);
  switch (x.k) {
    case 'mul': {
      const l = valOf(ks, x.l);
      const r = valOf(ks, x.r);
      if (l && r && l.t !== 's' && r.t !== 's') return true;
      return hasMatrixProduct(ks, x.l) || hasMatrixProduct(ks, x.r);
    }
    case 'add':
    case 'sub':
    case 'div':
      return hasMatrixProduct(ks, x.l) || hasMatrixProduct(ks, x.r);
    case 'neg':
      return hasMatrixProduct(ks, x.e);
    case 'pow': {
      const v = valOf(ks, x.e);
      return !!v && v.t === 'm';
    }
    case 'tr':
    case 'det':
      return true;
    case 'entry':
      return hasMatrixProduct(ks, x.e);
    case 'lit':
      return x.rows.some((r) => r.some((c) => hasMatrixProduct(ks, c)));
    default:
      return false;
  }
}

/** les matrices nommées hors d'un déterminant (« Remplace d'abord A ») */
function matrixNames(e: MExpr, out = new Set<string>()): Set<string> {
  const x = strip(e);
  if (x.k === 'det') return out;
  if (x.k === 'name') {
    if (!/^I[₀-₉]*$/.test(x.n)) out.add(x.n);
    return out;
  }
  if (x.k === 'lit') x.rows.forEach((r) => r.forEach((c) => matrixNames(c, out)));
  else if ('l' in x) {
    matrixNames(x.l, out);
    matrixNames(x.r, out);
  } else if ('e' in x) matrixNames(x.e, out);
  return out;
}

/** I retirée des produits (I × A → A, I⁵⁰ → I) */
function dropIdentity(e: MExpr): { e: MExpr; dropped: number } {
  const x = strip(e);
  if (x.k === 'mul') {
    const fs = factors(x);
    const keep = fs.filter((f) => !isIdentityExpr(f));
    const dropped = fs.length - keep.length;
    if (!keep.length) return { e: { k: 'name', n: 'I' }, dropped };
    return { e: prodOf(keep), dropped };
  }
  if (x.k === 'pow' && isIdentityExpr(x)) return { e: { k: 'name', n: 'I' }, dropped: 1 };
  return { e: x, dropped: 0 };
}

const closedForm = (e: MExpr): boolean => isNumericLit(e) || numOf(e) !== null || isIdentityExpr(e);

const ONE_STEP: Cls = {
  ok: false,
  title: 'Une étape à la fois',
  message:
    'Je ne vois pas comment on passe de ta ligne précédente à celle-ci : écris une étape de plus.',
};
const NEED_DET: Cls = {
  ok: false,
  title: 'Le déterminant d’abord',
  message:
    'Calcule d’abord le déterminant : il dit si la matrice est inversible, et par quoi diviser.',
};

/**
 * Une matrice dont les coefficients sont ÉCRITS : une matrice de l'énoncé, I, une matrice
 * écrite avec des nombres, ou un calcul dont une ligne validée a donné la matrice (moteur v52).
 */
function knownLiteral(ks: KMatState, X0: MExpr): MVal | null {
  const X = strip(X0);
  if (isNumericLit(X) || isIdentityExpr(X)) return valOf(ks, X);
  if (X.k === 'name' && ks.givenLit.has(X.n)) return valOf(ks, X);
  const s = sig(X);
  for (const o of new Set(ks.objects.values()))
    if (o.form && (isNumericLit(o.form) || isIdentityExpr(o.form)) && o.written.has(s))
      return o.value;
  return null;
}

/**
 * Moteur v52 — un coefficient « (M²)₁₃ » : lu sur la matrice écrite, ou calculé seul, la
 * ligne i de la première matrice par la colonne j de la seconde (les deux matrices sont
 * écrites : M, et M² pour (M³)₁₁).
 */
function entryStep(ks: KMatState, P: Extract<MExpr, { k: 'entry' }>, N: MExpr): Cls | null {
  const n = numOf(N);
  if (n !== null && knownLiteral(ks, P.e)) return pass('entry');
  const fs = efactors(P.e);
  const i = P.i - 1;
  const j = P.j - 1;
  for (let s = 1; s < fs.length; s++) {
    const L = knownLiteral(ks, prodOf(fs.slice(0, s)));
    const R = knownLiteral(ks, prodOf(fs.slice(s)));
    if (!L || !R || L.t !== 'm' || R.t !== 'm' || L.c !== R.r) continue;
    if (i >= L.r || j >= R.c) continue;
    const ex: Q.Rat[] = [];
    for (let k = 0; k < L.c; k++) ex.push(Q.mul(L.a[i]![k]!, R.a[k]![j]!));
    if (opCount(N) > 0 && termsMatch(N, ex)) return pass('entryProduct');
  }
  if (n !== null && hasMatrixProduct(ks, P.e))
    return {
      ok: false,
      title: 'Montre le calcul',
      message: `Écris le coefficient (${P.i}, ${P.j}) : la ligne ${P.i} de la première matrice par la colonne ${P.j} de la seconde, terme à terme.`,
    };
  return null;
}

function classify(ks: KMatState, obj: MatObject, P0: MExpr, N0: MExpr, depth = 0): Cls {
  const P = strip(P0);
  const N = strip(N0);
  if (canon(P) === canon(N)) return pass();
  if (P.k === 'entry') {
    const c = entryStep(ks, P, N);
    if (c) return c;
  }
  // AX = B → X = A⁻¹B
  const eq = ks.equation;
  if (eq && obj.target && canon(P) === eq.unknown) {
    const inv: MExpr = { k: 'pow', e: eq.coef, n: -1, text: '⁻¹' };
    const want: MExpr =
      eq.side === 'left' ? { k: 'mul', l: inv, r: eq.right } : { k: 'mul', l: eq.right, r: inv };
    if (sig(N) === sig(want)) return pass('solve');
    return {
      ok: false,
      title: 'Isole X',
      message:
        eq.side === 'left'
          ? 'Pour isoler X dans AX = B, multiplie à gauche par A⁻¹ : X = A⁻¹B.'
          : 'Pour isoler X dans XA = B, multiplie à droite par A⁻¹ : X = BA⁻¹.',
    };
  }
  // A² = A × A, A¹⁰¹ = (A²)⁵⁰ × A
  const pp = powerFactors(P);
  const pn = powerFactors(N);
  if (
    pp &&
    pn &&
    pp.length === pn.length &&
    pp.every((f, i) => f.base === pn[i]!.base && f.n === pn[i]!.n)
  )
    return pass('power');
  // I × A = A
  const dp = dropIdentity(P);
  const dn = dropIdentity(N);
  if (dp.dropped > dn.dropped && sig(dp.e) === sig(dn.e)) return pass('identity');
  // remplacer (ou écrire la formule de l'inverse)
  const s = subst(ks, P, N);
  if (s === 'same') return pass();
  if (s === 'ok') return pass('substitute');
  if (s === 'inverse') return pass('inverse');
  if (s === 'needDet') return NEED_DET;
  // A⁻¹ : la formule d'une 2 × 2, ou un produit AM = I écrit avant
  if (P.k === 'pow' && P.n === -1) {
    const base = valOf(ks, P.e);
    const nv = valOf(ks, N);
    if (nv && base) {
      const isI = (e: MExpr): boolean => {
        if (isIdentityExpr(e)) return true;
        const v = valOf(ks, e);
        return !!v && isNumericLit(e) && valEq(v, { t: 'id', s: Q.ONE });
      };
      const checked = ks.lines.some(({ members }) => {
        const head = efactors(members[0]!);
        if (head.length !== 2) return false;
        const h0 = valOf(ks, head[0]!);
        const h1 = valOf(ks, head[1]!);
        if (!h0 || !h1) return false;
        const pair = (valEq(h0, base) && valEq(h1, nv)) || (valEq(h0, nv) && valEq(h1, base));
        return pair && isI(members[members.length - 1]!);
      });
      if (checked) return pass('inverseCheck');
      if (closedForm(N))
        return base.t === 'm' && base.r === 2 && !detKnown(ks, P.e)
          ? NEED_DET
          : {
              ok: false,
              title: 'Montre le calcul',
              message:
                base.t === 'm' && base.r === 2
                  ? 'Écris l’inverse avec la formule : (1 ÷ det A) × [d, −b ; −c, a].'
                  : 'Vérifie d’abord que le produit de la matrice par celle que tu proposes donne I.',
            };
    }
  }
  // déterminant 2 × 2
  if (P.k === 'det') {
    const m = valOf(ks, P.e);
    if (m && m.t === 'm' && m.r === 2 && m.c === 2) {
      const ex = [Q.mul(m.a[0]![0]!, m.a[1]![1]!), Q.neg(Q.mul(m.a[0]![1]!, m.a[1]![0]!))];
      if (opCount(N) > 0 && termsMatch(N, ex)) return pass('det');
      if (numOf(N) !== null)
        return {
          ok: false,
          title: 'Montre le calcul',
          message: 'Écris le calcul du déterminant : a × d − b × c.',
        };
    }
  }
  // transposer
  if (P.k === 'tr' && N.k === 'lit' && isNumericLit(N)) {
    if (strip(P.e).k !== 'lit' && !(strip(P.e).k === 'name')) return ONE_STEP;
    return pass('transpose');
  }
  // ligne par colonne
  const pe = productExpansion(ks, P, N);
  if (pe === 'ok') return pass('product');
  if (pe && isNumericLit(N))
    return {
      ok: false,
      title: 'Montre le calcul',
      message: 'Écris le produit ligne par colonne avant de donner le résultat.',
    };
  if (pe)
    return {
      ok: false,
      title: 'Montre le calcul',
      message: `Écris le coefficient (${pe.i + 1}, ${pe.j + 1}) : la ligne ${pe.i + 1} de la première matrice par la colonne ${pe.j + 1} de la seconde, terme à terme.`,
    };
  // une somme de matrices écrite coefficient par coefficient
  if (entrywiseExpansion(ks, P, N)) return pass('entrywise');
  // développer : A(A − 2I) = A² − 2A (des matrices des deux côtés, pas un calcul de nombres)
  const np = nf(ks, P);
  if (np && [...np.keys()].some((w) => w !== '') && sameNf(np, nf(ks, N))) return pass('expand');
  if (depth < 4) {
    // deux membres d'une somme : chacun son pas
    if (P.k === N.k && (P.k === 'add' || P.k === 'sub')) {
      const n = N as Extract<MExpr, { k: 'add' | 'sub' }>;
      const l = classify(ks, obj, P.l, n.l, depth + 1);
      const r = classify(ks, obj, P.r, n.r, depth + 1);
      if (!l.ok) return l;
      if (!r.ok) return r;
      return pass(...l.kinds, ...r.kinds);
    }
    // un facteur commun devant ou derrière (1 ÷ (−2) × …) : on juge ce qui change
    if (P.k === 'mul' || N.k === 'mul') {
      const fp = efactors(P);
      const fn = efactors(N);
      let i = 0;
      while (i < fp.length - 1 && i < fn.length - 1 && sig(fp[i]!) === sig(fn[i]!)) i++;
      let j = 0;
      while (
        j < fp.length - 1 - i &&
        j < fn.length - 1 - i &&
        sig(fp[fp.length - 1 - j]!) === sig(fn[fn.length - 1 - j]!)
      )
        j++;
      const mp = fp.slice(i, fp.length - j);
      const mn = fn.slice(i, fn.length - j);
      if (i + j > 0 && mp.length && mn.length)
        return classify(ks, obj, prodOf(mp), prodOf(mn), depth + 1);
    }
  }
  // des coefficients calculés
  if (P.k === 'lit' && N.k === 'lit' && opCount(N) < opCount(P)) return pass('compute');
  // somme, nombre × matrice : coefficient par coefficient
  if (N.k === 'lit' && isLinear(ks, P) && !isNumericLit(P)) {
    const names = [...matrixNames(P)];
    if (names.length)
      return {
        ok: false,
        title: 'Montre le calcul',
        message: `Remplace d’abord ${names.join(', ')} par sa matrice, puis calcule coefficient par coefficient.`,
      };
    return pass('entrywise');
  }
  // factoriser par un nombre : [5, 10 ; 10, 20] = 5 × [1, 2 ; 2, 4]
  if (P.k === 'lit' && N.k === 'mul') {
    const fs = factors(N);
    if (fs.length === 2 && numOf(fs[0]!) !== null && strip(fs[1]!).k === 'lit')
      return pass('factor');
  }
  // calculer : mêmes matrices nommées, moins d'opérations, aucun produit de matrices sauté
  if (
    !hasMatrixProduct(ks, P) &&
    [...namesIn(N)].every((n) => namesIn(P).has(n)) &&
    [...matrixNames(P)].every((n) => matrixNames(N).has(n)) &&
    opCount(N) < opCount(P)
  )
    return pass('compute');
  if (closedForm(N) && hasMatrixProduct(ks, P))
    return {
      ok: false,
      title: 'Montre le calcul',
      message: 'Écris le produit ligne par colonne avant de donner le résultat.',
    };
  return ONE_STEP;
}

/** un calcul qui sert la question : un calcul du corrigé, ou une puissance en chemin */
function isRelevant(ks: KMatState, head: MExpr): boolean {
  if (ks.relevantKeys.has(canon(strip(head)))) return true;
  // l'inverse cherché : vérifier un produit A × M (ou M × A)
  const st = strip(ks.target.start);
  if (st.k === 'pow' && st.n === -1) {
    const fs = efactors(head);
    if (fs.length === 2 && fs.some((f) => sig(f) === sig(st.e))) return true;
  }
  const pf = powerFactors(head);
  if (!pf || pf.length !== 1) return false;
  const want = powerFactors(ks.target.start);
  return !!want && want.some((f) => f.base === pf[0]!.base && f.n >= pf[0]!.n);
}

/* ------------------------------ jugement ------------------------------ */

/** l'objet dont parle une tête de ligne : par son nom, ou par une forme déjà écrite */
function findObject(ks: KMatState, head0: MExpr): MatObject | undefined {
  const head = strip(head0);
  const obj = ks.objects.get(canon(head));
  if (obj) return obj;
  const h = sig(head);
  for (const o of new Set(ks.objects.values())) if (o.form && sig(o.form) === h) return o;
  return undefined;
}

function strictPrefixOfKnown(ks: KMatState, A: string[]): boolean {
  return ks.known.some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}

/** pourquoi une matrice écrite n'a pas la bonne valeur (sans la donner) */
function wrongWhy(ks: KMatState, obj: MatObject, v: MVal): { title: string; message: string } {
  const want = obj.value;
  // moteur v52 : le coefficient de M au lieu de celui de M²
  const st = strip(obj.start);
  if (st.k === 'entry' && v.t === 's') {
    const pw = strip(st.e);
    if (pw.k === 'pow' && pw.n >= 2) {
      const b = valOf(ks, { k: 'entry', e: pw.e, i: st.i, j: st.j });
      if (b && b.t === 's' && Q.eq(b.q, v.q) && !valEq(b, want))
        return {
          title: 'La puissance',
          message: `C’est le coefficient de la matrice elle-même : il faut celui de sa puissance ${pw.text}.`,
        };
    }
  }
  if (want.t === 'm' && v.t === 'm' && (want.r !== v.r || want.c !== v.c))
    return {
      title: 'Pas la bonne taille',
      message: `Le résultat est une matrice ${want.r} × ${want.c} (${want.r} ligne${want.r > 1 ? 's' : ''}, ${want.c} colonne${want.c > 1 ? 's' : ''}).`,
    };
  const fs = efactors(obj.start);
  if (fs.length === 2) {
    const a = valOf(ks, fs[0]!);
    const b = valOf(ks, fs[1]!);
    if (a && b && a.t === 'm' && b.t === 'm') {
      try {
        if (valEq(v, mulVals(b, a)) && !valEq(v, want))
          return {
            title: 'Pas le bon ordre',
            message:
              'Le produit de matrices dépend de l’ordre : tu as calculé le produit dans l’autre sens.',
          };
      } catch {
        /* produit inverse impossible */
      }
      if (a.r === b.r && a.c === b.c && v.t === 'm' && v.r === a.r && v.c === a.c) {
        const had = a.a.map((row, i) => row.map((x, j) => Q.mul(x, b.a[i]![j]!)));
        if (valEq(v, { t: 'm', r: a.r, c: a.c, a: had }))
          return {
            title: 'Ligne par colonne',
            message:
              'Un produit de matrices ne se calcule pas coefficient par coefficient : c’est ligne par colonne.',
          };
      }
    }
  }
  if (want.t === 'm' && v.t === 'm')
    for (let i = 0; i < want.r; i++)
      for (let j = 0; j < want.c; j++)
        if (!Q.eq(want.a[i]![j]!, v.a[i]![j]!))
          return {
            title: 'Calcul faux',
            message: `Le coefficient (${i + 1}, ${j + 1}) ne va pas.`,
          };
  if (want.t === 'id' && v.t === 'm')
    return { title: 'Calcul faux', message: 'Ce n’est pas la bonne matrice.' };
  return { title: 'Calcul faux', message: 'Ce n’est pas la bonne valeur.' };
}

export function kmatJudge(ks: KMatState, chips: Chip[], opts?: { verify?: boolean }): KMatVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = strictPrefixOfKnown(ks, A);
  let members: MExpr[];
  try {
    members = parseMatLine(A);
  } catch (e) {
    if (e instanceof MatIncomplete || (prefix && !opts?.verify)) {
      if (opts?.verify)
        return { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' };
      return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
    }
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça ne se lit pas',
      message: e instanceof MatSyntaxError ? e.message : String(e),
      badChip: chips.length - 1,
    };
  }
  if (members.length < 2) {
    if (opts?.verify)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une égalité',
        message: 'Écris une égalité : « AB = … ».',
      };
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const v = judgeParsed(ks, A, members);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

function judgeParsed(ks: KMatState, A: string[], members: MExpr[]): KMatVerdict {
  const line = lineOf(A);
  const base = { atoms: A, line };

  if (ks.givenTexts.has(matText(A)))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà dans l’énoncé : écris le calcul suivant.',
    };
  // l'objet : la cible, une matrice définie, un calcul déjà commencé, un nom nouveau, ou un calcul neuf
  const head = strip(members[0]!);
  const key = canon(head);
  let obj = findObject(ks, head);
  let define: MMove['define'];
  let seq: MExpr[];
  const steps: StepKind[] = [];
  if (obj) seq = [obj.form ?? obj.start, ...members.slice(1)];
  else if (head.k === 'name' && !ks.env.has(head.n) && !/^I/.test(head.n)) {
    // un nom nouveau : π₁ = π₀P — défini par un calcul, jamais par une valeur sortie de nulle part
    if (closedForm(members[1]!))
      return {
        kind: 'unjustified',
        ...base,
        title: 'D’où vient-elle ?',
        message: `Un nouveau nom se définit par un calcul : écris d’abord ce que vaut ${head.n} avec les matrices de l’énoncé.`,
      };
    let v: MVal;
    try {
      v = evalM(members[1]!, ks.env);
    } catch (e) {
      return { kind: 'false', ...base, title: 'Calcul impossible', message: (e as Error).message };
    }
    define = { name: head.n, value: v };
    obj = {
      key,
      start: members[1]!,
      value: v,
      form: null,
      written: new Set([sig(head)]),
      target: false,
      relevant: isRelevant(ks, head),
    };
    seq = members.slice(1);
    steps.push('define');
  } else {
    let v: MVal;
    try {
      v = evalM(head, ks.env);
    } catch (e) {
      return trapOr(ks, key, null, base, {
        title:
          e instanceof MatError && e.code === 'size'
            ? 'Les tailles ne vont pas'
            : 'Calcul impossible',
        message: (e as Error).message,
      });
    }
    obj = {
      key,
      start: head,
      value: v,
      form: null,
      written: new Set([sig(head)]),
      target: false,
      relevant: isRelevant(ks, head),
    };
    seq = members;
  }

  // chaque membre vaut la même chose
  for (const m of members.slice(define ? 1 : 0)) {
    let v: MVal;
    try {
      v = evalM(m, ks.env);
    } catch (e) {
      return trapOr(ks, obj.key, null, base, {
        title:
          e instanceof MatError && e.code === 'size'
            ? 'Les tailles ne vont pas'
            : 'Calcul impossible',
        message: (e as Error).message,
      });
    }
    if (!valEq(v, obj.value)) return trapOr(ks, obj.key, v, base, wrongWhy(ks, obj, v));
  }

  // un calcul juste, mais qui ne sert pas pour la question
  if (!obj.relevant)
    return {
      kind: 'useless',
      ...base,
      title: 'Juste, mais à côté',
      message: 'C’est juste, mais ce calcul ne sert pas pour la question.',
    };
  // chaque maillon est un pas
  for (let i = 0; i + 1 < seq.length; i++) {
    const c = classify(ks, obj, seq[i]!, seq[i + 1]!);
    if (!c.ok)
      return {
        kind: 'unjustified',
        ...base,
        title: c.title,
        message: c.message,
      };
    steps.push(...c.kinds);
  }
  const last = members[members.length - 1]!;
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cette ligne redit ce qui est déjà écrit.',
    };
  const final =
    obj.target &&
    !ks.concluded &&
    (sig(last) === sig(ks.answerLast) || (closedFormValue(ks.answerLast) && closedFormValue(last)));
  const hora = steps.reduce((s, k) => s + STEP[k].hora, 0);
  const main = steps.reduce((m, k) => (STEP[k].hora >= STEP[m].hora ? k : m), steps[0]!);
  const why =
    [...new Set(steps)].map((k) => STEP[k].why).join(' ') + (final ? ' C’est la réponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: { id: MATRIX_STEP_IDS[main], card: CARD, label: STEP[main].label, hora, why },
    mmove: { key: obj.key, members, ...(define ? { define } : {}), ...(final ? { final } : {}) },
  };
}

const closedFormValue = (e: MExpr): boolean =>
  isNumericLit(e) || numOf(e) !== null || isIdentityExpr(e);

function trapOr(
  ks: KMatState,
  key: string,
  v: MVal | null,
  base: { atoms: string[]; line: Line },
  why: { title: string; message: string },
): KMatVerdict {
  const text = matText(base.atoms);
  const t = ks.traps.find(
    (t) => t.text === text || (t.key === key && !!v && !!t.value && valEq(v, t.value)),
  );
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, title: why.title, message: why.message };
}

export function kmatCommit(ks: KMatState, v: KMatVerdict): void {
  const m = v.mmove;
  if (!m) return;
  let obj = ks.objects.get(m.key);
  if (m.define) {
    ks.env.set(m.define.name, m.define.value);
    if (!obj) {
      obj = {
        key: m.key,
        start: m.members[1]!,
        value: m.define.value,
        form: null,
        written: new Set(),
        target: false,
        relevant: true,
      };
      ks.objects.set(m.key, obj);
    }
  }
  if (!obj) obj = findObject(ks, m.members[0]!);
  if (!obj) {
    const head = m.members[0]!;
    obj = {
      key: m.key,
      start: head,
      value: evalM(head, ks.env),
      form: null,
      written: new Set(),
      target: false,
      relevant: true,
    };
    ks.objects.set(m.key, obj);
  }
  m.members.forEach((x) => obj!.written.add(sig(x)));
  obj.form = m.members[m.members.length - 1]!;
  ks.lines.push({ key: obj.key, members: m.members });
  if (m.final) ks.concluded = true;
}

export function kmatGoalReached(ks: KMatState): boolean {
  return ks.concluded;
}

function readText(text: string): MExpr[] | null {
  try {
    return parseText(text);
  } catch {
    return null;
  }
}

/** la ligne est vraie (tous ses membres valent la même chose, la cible si elle en parle) */
export function kmatHolds(ks: KMatState, text: string): boolean {
  const ms = readText(text);
  if (!ms || ms.length < 2) return false;
  try {
    const head = strip(ms[0]!);
    const obj = findObject(ks, head);
    const ref = obj ? obj.value : evalM(head, ks.env);
    return ms.every((m) => valEq(evalM(m, ks.env), ref));
  } catch {
    return false;
  }
}

/** la ligne donne la réponse : elle parle de la cible, et sa valeur est la bonne */
export function kmatIsAnswer(ks: KMatState, text: string): boolean {
  const ms = readText(text);
  if (!ms || ms.length < 2) return false;
  const obj = findObject(ks, ms[0]!);
  if (!obj || obj !== ks.target) return false;
  try {
    return ms.slice(1).every((m) => valEq(evalM(m, ks.env), ks.target.value));
  } catch {
    return false;
  }
}

/** une ligne déjà établie : son objet a écrit sa dernière forme */
export function kmatEstablished(ks: KMatState, text: string): boolean {
  const ms = readText(text);
  if (!ms || ms.length < 2) return false;
  const obj = findObject(ks, ms[0]!);
  return !!obj && obj.written.has(sig(ms[ms.length - 1]!));
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la première ligne du corrigé pas encore écrite */
export function kmatNextMove(
  ks: KMatState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  for (const t of ks.path) {
    if (kmatEstablished(ks, t)) continue;
    const atoms = kmatLineAtoms(t);
    const v = kmatJudge(ks, chipsOf(atoms), { verify: true });
    return { line: kmatLine(t), text: matText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kmatSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KMatVerdict }[];
  reached: boolean;
} {
  const ks = createKMatState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KMatVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = kmatLineAtoms(t);
    const v = kmatJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kmatCommit(ks, v);
    lines.push({ line: kmatLine(t), text: matText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}

/* ------------------------------ palette ------------------------------ */

/** les opérations du calcul matriciel, dans l'ordre d'affichage */
export const MATRIX_OPS = ['[', ']', ',', ';', '+', '−', '×', '÷', '(', ')', '²', '⁻¹', 'ᵀ', '='];

/** les bouts de la feuille : noms, nombres (triés), exposants et opérations utiles */
/** moteur v52 : les coefficients écrits (« (M²)₁₃ »), chacun un bout de la palette */
export function kmatEntryTexts(atoms: string[]): string[] {
  const out: string[] = [];
  atoms.forEach((a, k) => {
    if (!isEntryAtom(a) || atoms[k - 1] !== ')') return;
    let depth = 0;
    let o = k - 1;
    for (; o >= 0; o--) {
      if (atoms[o] === ')') depth++;
      if (atoms[o] === '(') depth--;
      if (depth === 0) break;
    }
    if (o < 0) return;
    const t = matText(atoms.slice(o, k + 1));
    if (!out.includes(t)) out.push(t);
  });
  return out;
}

export function kmatPaletteParts(model: ScratchModel): {
  names: string[];
  words: string[];
  numbers: string[];
  ops: string[];
  /** moteur v52 : les coefficients (« (M²)₁₃ ») */
  entries: string[];
} {
  const spec = model.kernel!;
  const texts = [
    spec.start,
    ...(spec.path ?? []),
    spec.answer,
    ...Object.values(spec.matrices ?? {}),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const atoms = texts.flatMap((t) => {
    try {
      return kmatLineAtoms(t);
    } catch {
      return [];
    }
  });
  const names: string[] = [];
  const add = (n: string) => {
    if (!names.includes(n)) names.push(n);
  };
  Object.keys(spec.matrices ?? {}).forEach(add);
  atoms.filter(isMatName).forEach(add);
  const words = atoms.includes('det') ? ['det'] : [];
  const nums = [...new Set(atoms.filter((a) => MAT_NUM_RE.test(a)))];
  const val = (s: string) => Number(s.replace('−', '-').replace(',', '.'));
  nums.sort((a, b) => val(a) - val(b) || a.localeCompare(b));
  const used = new Set(atoms);
  const sups = [...new Set(atoms.filter((a) => isSupAtom(a) && !['²', '⁻¹', 'ᵀ'].includes(a)))];
  const ops = [...MATRIX_OPS.filter((o) => used.has(o) || o === '='), ...sups];
  return { names, words, numbers: nums, ops, entries: kmatEntryTexts(atoms) };
}

/** pour les tests : la matrice identité */
export const _identity = identity;
